import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { Badge } from '@shadcn/components/ui/badge'
import ReactSelect from '@shadcn/components/ui/react-select'
import { getTierBadgeStyle, getTierLabel } from './helpers'

/**
 * Product-aware multi-select for picking nesting prefixes.
 * Options are existing rule prefixes labelled "CODE · Friendly Label (N products)",
 * searchable by product name OR prefix code. Stores/returns an array of prefix strings.
 */
export function PrefixMultiSelect ({ value, onChange, rules, prefixProducts, placeholder, ariaLabel }) {
  const options = (rules || []).map((r) => {
    const entry = prefixProducts?.[r.prefix]
    const labelText = entry?.derivedLabel && entry.derivedLabel !== r.prefix ? `${r.prefix} · ${entry.derivedLabel}` : r.prefix
    const count = entry?.matchCount ?? 0
    return {
      value: r.prefix,
      label: count ? `${labelText} (${count})` : labelText,
      search: [r.prefix, entry?.derivedLabel, ...((entry?.products || []).map((p) => p.productName))]
        .filter(Boolean)
        .join(' ')
        .toLowerCase(),
    }
  })
  const optionByValue = new Map(options.map((o) => [o.value, o]))
  const selected = (value || []).map(
    (p) => optionByValue.get(p) || { value: p, label: `${p} (unknown)`, search: String(p).toLowerCase() },
  )

  return (
    <ReactSelect
      isMulti
      isSearchable
      value={selected}
      onChange={(opts) => onChange((opts || []).map((o) => o.value))}
      options={options}
      placeholder={placeholder || 'Search products or prefixes…'}
      aria-label={ariaLabel}
      filterOption={(option, raw) => {
        const q = (raw || '').toLowerCase().trim()
        return !q || (option.data?.search || '').includes(q)
      }}
    />
  )
}

export function CsvTextarea ({ value, onChange, placeholder }) {
  const ref = useRef(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const hasValue = String(value || '').trim().length > 0
    el.style.height = 'auto'
    el.style.height = hasValue ? `${Math.max(el.scrollHeight, 42)}px` : '42px'
  }, [value])

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={1}
      wrap="soft"
      style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
      className="w-full resize-y overflow-hidden rounded-md border border-black bg-background px-3 py-2 text-sm leading-6 ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
    />
  )
}

export function PrefixWrapList ({ prefixes, suffix = null }) {
  const text = prefixes.join(',')

  return (
    <div
      className="min-w-0 max-w-full font-mono text-[11px] leading-5 text-gray-800"
      style={{ hyphens: 'none' }}
    >
      <span className="mr-2">{text}</span>
      {suffix ? <span className="inline-block align-middle">{suffix}</span> : null}
    </div>
  )
}

export function LabeledPrefixesLine ({ label, prefixes, listClassName = '', title: titleProp }) {
  const [expanded, setExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const textRef = useRef(null)
  const rowRef = useRef(null)
  const joined = prefixes.join(', ')
  const title = titleProp ?? joined

  const measureCollapsed = useCallback(() => {
    const el = textRef.current
    if (!el || expanded) return
    const w = el.clientWidth
    if (w < 4) return
    setHasOverflow(el.scrollWidth > w + 0.5)
  }, [expanded])

  useLayoutEffect(() => {
    measureCollapsed()
    const row = rowRef.current
    if (!row || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => measureCollapsed())
    ro.observe(row)
    return () => ro.disconnect()
  }, [joined, expanded, measureCollapsed])

  if (!prefixes.length) return null

  const showToggle = expanded || hasOverflow
  const listLineWrapClass = expanded ? 'overflow-x-auto' : 'overflow-hidden'
  const listTextClass = expanded
    ? ['shrink-0 whitespace-nowrap text-gray-600', listClassName].filter(Boolean).join(' ')
    : ['min-w-0 flex-1 truncate text-gray-600', listClassName].filter(Boolean).join(' ')

  return (
    <div
      ref={rowRef}
      className="flex min-w-0 w-full max-w-full flex-nowrap items-center gap-x-1.5 text-[11px] text-gray-700"
      title={title}
    >
      {label ? <span className="shrink-0 font-medium text-gray-800">{label}</span> : null}
      <span className={['flex min-h-5 min-w-0 flex-1 flex-nowrap items-center gap-1', listLineWrapClass].join(' ')}>
        <span ref={textRef} className={listTextClass}>{joined}</span>
        {showToggle ? (
          <button
            type="button"
            className="shrink-0 border-0 bg-transparent p-0 font-semibold text-slate-600 underline underline-offset-2 hover:text-slate-800 cursor-pointer"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setExpanded((v) => !v)
            }}
          >
            {expanded ? 'show less' : '... more'}
          </button>
        ) : null}
      </span>
    </div>
  )
}

export function TierBadgeOnly ({ tier, tierLabels }) {
  return (
    <div className="flex min-h-7 min-w-0 flex-col justify-center border-l border-gray-200 px-1.5 py-0.5">
      {tier != null && tier !== '' ? (
        <Badge
          variant="outline"
          className="w-fit max-w-[14rem] shrink-0 truncate text-[11px]"
          style={getTierBadgeStyle(tier)}
        >
          {getTierLabel(tierLabels, tier)}
        </Badge>
      ) : (
        <span className="shrink-0 text-gray-400 text-xs">-</span>
      )}
    </div>
  )
}
